import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Sparkles, ScrollText, ChevronRight, ChevronLeft, Pause, Play } from "lucide-react";

const DURATION = 9000;

const ITEMS = [
  { type: "quran", text: "﴿وَمَا تُقَدِّمُوا لِأَنفُسِكُم مِّنْ خَيْرٍ تَجِدُوهُ عِندَ اللَّهِ ۖ إِنَّ اللَّهَ بِمَا تَعْمَلُونَ بَصِيرٌ﴾", source: "سورة البقرة — الآية ١١٠" },
  { type: "quran", text: "﴿فَمَن يَعْمَلْ مِثْقَالَ ذَرَّةٍ خَيْرًا يَرَهُ﴾", source: "سورة الزلزلة — الآية ٧" },
  { type: "hadith", text: "«خَيْرُ النَّاسِ أَنْفَعُهُمْ لِلنَّاسِ»", source: "حديث شريف" },
  { type: "quran", text: "﴿وَافْعَلُوا الْخَيْرَ لَعَلَّكُمْ تُفْلِحُونَ﴾", source: "سورة الحج — الآية ٧٧" },
  { type: "phrase", text: "عطاؤكم لا يُقاس بالساعة وحدها، بل بالأثر الذي يبقى.", source: "من عبارات عطاء" },
  { type: "quran", text: "﴿وَأَحْسِنُوا ۛ إِنَّ اللَّهَ يُحِبُّ الْمُحْسِنِينَ﴾", source: "سورة البقرة — الآية ١٩٥" },
  { type: "phrase", text: "يدٌ تُعطي خيرٌ من ألف يدٍ تنتظر… فليكن عطاؤكم سبّاقاً.", source: "من عبارات عطاء" },
  { type: "quran", text: "﴿مَّثَلُ الَّذِينَ يُنفِقُونَ أَمْوَالَهُمْ فِي سَبِيلِ اللَّهِ كَمَثَلِ حَبَّةٍ أَنبَتَتْ سَبْعَ سَنَابِلَ فِي كُلِّ سُنبُلَةٍ مِّائَةُ حَبَّةٍ ۗ وَاللَّهُ يُضَاعِفُ لِمَن يَشَاءُ﴾", source: "سورة البقرة — الآية ٢٦١" },
  { type: "phrase", text: "ازرعوا الخير ولا تنتظروا الثمر… فالله لا يُضيع أجر المحسنين.", source: "من عبارات عطاء" },
  { type: "quran", text: "﴿لَن تَنَالُوا الْبِرَّ حَتَّىٰ تُنفِقُوا مِمَّا تُحِبُّونَ ۚ وَمَا تُنفِقُوا مِنْ شَيْءٍ فَإِنَّ اللَّهَ بِهِ عَلِيمٌ﴾", source: "سورة آل عمران — الآية ٩٢" },
  { type: "phrase", text: "بصمتكم في الخير تبقى — كل فرصة تُوثَّق اليوم أثرٌ يدوم.", source: "من عبارات عطاء" },
  { type: "phrase", text: "التطوع رسالة إنسانية، ووطنٌ يزدهر بأبنائه وبناته.", source: "من عبارات عطاء" },
];

const TYPE_META = {
  quran: { icon: BookOpen, label: "آية كريمة" },
  hadith: { icon: ScrollText, label: "حديث شريف" },
  phrase: { icon: Sparkles, label: "عبارة تحفيزية" },
};

export function InspirationBanner({ testid = "inspiration-banner" }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = (d) => setIdx((i) => (i + d + ITEMS.length) % ITEMS.length);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % ITEMS.length), DURATION);
    return () => clearInterval(t);
  }, [paused, idx]);

  const item = ITEMS[idx];
  const Meta = TYPE_META[item.type];
  const Icon = Meta.icon;

  return (
    <div data-testid={testid} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      className={`insp-wrap ${paused ? "insp-paused" : ""} rounded-3xl p-6 sm:p-7 mb-6 text-white relative overflow-hidden shadow-lg shadow-turq-900/20`}
      style={{ background: "linear-gradient(100deg,#0B6E7A,#12939E 55%,#C1502F)" }}>
      <style>{`
        @keyframes insp-progress { from { width: 0 } to { width: 100% } }
        .insp-progress { animation: insp-progress ${DURATION}ms linear forwards; }
        .insp-paused .insp-progress { animation-play-state: paused; }
      `}</style>
      <div className="absolute -left-10 -top-16 w-52 h-52 rounded-full bg-white/10" />
      <div className="absolute left-28 -bottom-20 w-36 h-36 rounded-full bg-white/10" />
      <div className="absolute right-6 -top-8 font-head font-black text-[110px] leading-none text-white/10 select-none">❞</div>

      <div className="relative flex items-start gap-4">
        <div className="hidden sm:flex w-12 h-12 rounded-2xl bg-white/15 backdrop-blur items-center justify-center shrink-0">
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-h-[84px]">
          <AnimatePresence mode="wait">
            <motion.div key={idx} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.55, ease: "easeOut" }}>
              <span data-testid="inspiration-type" className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-[10px] font-bold px-3 py-1 rounded-full mb-2.5">
                <Icon className="w-3 h-3" /> {Meta.label}
              </span>
              <p data-testid="inspiration-text" className="font-head font-bold text-base sm:text-xl leading-loose">{item.text}</p>
              <span data-testid="inspiration-source" className="text-[11px] text-white/80 font-bold">{item.source}</span>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button data-testid="inspiration-pause-btn" onClick={() => setPaused(!paused)}
            title={paused ? "تشغيل العرض التلقائي" : "إيقاف مؤقت"}
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 transition-colors flex items-center justify-center">
            {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
          <button data-testid="inspiration-next-btn" onClick={() => go(1)} title="التالي"
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 transition-colors flex items-center justify-center">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button data-testid="inspiration-prev-btn" onClick={() => go(-1)} title="السابق"
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 transition-colors flex items-center justify-center">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="relative flex items-center gap-3 mt-4">
        <div className="flex gap-1.5">
          {ITEMS.map((it, i) => (
            <button key={it.text} data-testid={`inspiration-dot-${i}`} onClick={() => setIdx(i)} aria-label={`عبارة ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"}`} />
          ))}
        </div>
        <div className="flex-1 h-1 rounded-full bg-white/20 overflow-hidden">
          {!paused && <div key={idx} data-testid="inspiration-progress" className="insp-progress h-full bg-white/70 rounded-full" />}
        </div>
        <span className="text-[10px] font-bold text-white/70 whitespace-nowrap">{idx + 1} / {ITEMS.length}</span>
      </div>
    </div>
  );
}
